/**
 * Projects in the browser: opening one as an EditorProject over IndexedDB,
 * connecting the editor to it in the page, and moving projects in and out
 * as .zip files or folders.
 */
import { guideFiles, isShaderPath, normalizeProjectPath, type ProjectFile } from '@animo-editor/format';
import { EditorProject, openSession, PROJECT_FILE, type ClientMessage, type Storage } from '@animo-editor/project';
import { strToU8, unzipSync, zipSync, type Zippable } from 'fflate';
import type { FolderScripts } from './folder.js';
import { scriptCandidate } from './folder.js';
import { rootPrefix } from './files-url.js';

export { rootPrefix };

/**
 * Opens a project. Its root folder, from project.json, is a prefix in
 * the same storage, such as a subfolder of a folder project's folder.
 */
export function openProject(record: { storageOf(prefix?: string): Storage }, options: { autosaveMs?: number } = {}): Promise<EditorProject> {
  return EditorProject.load({ projectStorage: record.storageOf(), projectName: PROJECT_FILE, openRoot: (root) => record.storageOf(`${root}/`) }, options);
}

/**
 * The editor's connection to a project in the same page. Messages are copied
 * on the way, as a socket would, so the editor's replica never shares objects
 * with the project's documents. With `scripts` (a folder project), the
 * Scripts section can list and create the game's scripts, as with the dev
 * server; `scriptsChanged` sends it a fresh list.
 */
export function localTransport(project: EditorProject, scripts?: FolderScripts) {
  let push: ((msg: unknown) => void) | undefined;
  return {
    lostMessage: 'The project closed. Reload this page.',
    open(h: { opened: () => void; message: (msg: unknown) => void; closed: () => void }) {
      let open = true;
      h.opened();
      const send = (msg: unknown) => {
        const copy = structuredClone(msg);
        queueMicrotask(() => open && h.message(copy));
      };
      push = send;
      const session = openSession(project, 'tab:1', send);
      const scriptRequest = async (msg: { t: string; req?: number; path?: unknown; text?: unknown }) => {
        const req = typeof msg.req === 'number' ? msg.req : 0;
        try {
          if (msg.t === 'scripts') send({ t: 'ack', req, rev: project.rev, scripts: await scripts!.list() });
          else send({ t: 'ack', req, rev: project.rev, script: await scripts!.create(String(msg.path), String(msg.text)) });
        } catch (e) {
          send({ t: 'reject', req, message: (e as Error).message });
        }
      };
      return {
        send: (msg: unknown) => {
          const m = msg as { t: string };
          if (scripts && (m.t === 'scripts' || m.t === 'createScript')) void scriptRequest(m);
          else void session.receive(structuredClone(msg) as ClientMessage);
        },
        ready: () => open,
      };
    },
    /** Sends the editor the current script list, after script files changed. */
    async scriptsChanged() {
      if (scripts && push) push({ t: 'scripts', scripts: await scripts.list() });
    },
  };
}

const skip = (path: string) => path.split('/').some((part) => part.startsWith('.') || part === 'node_modules' || part === '__MACOSX');

/**
 * Turns a set of files (from a .zip or a folder) into a project's files: finds
 * the shallowest project.json, makes paths relative to its folder, and drops
 * everything outside it, hidden files and node_modules.
 */
export function projectFiles(entries: Map<string, Uint8Array>): { files: Map<string, Uint8Array>; name: string } {
  const candidates = [...entries.keys()].filter((p) => (p === PROJECT_FILE || p.endsWith(`/${PROJECT_FILE}`)) && !skip(p));
  candidates.sort((a, b) => a.split('/').length - b.split('/').length);
  const found = candidates[0];
  if (!found) throw new Error('No project.json in it. A project is a folder with project.json at the top.');
  const base = found.slice(0, -PROJECT_FILE.length);
  const files = new Map<string, Uint8Array>();
  for (const [path, bytes] of entries) {
    if (!path.startsWith(base) || path.endsWith('/')) continue;
    const rel = path.slice(base.length);
    if (skip(rel)) continue;
    let normal: string;
    try {
      normal = normalizeProjectPath(rel);
    } catch {
      continue;
    }
    files.set(normal, bytes);
  }
  let name = base.replace(/\/$/, '').split('/').pop() || 'Imported project';
  try {
    const json = JSON.parse(new TextDecoder().decode(files.get(PROJECT_FILE))) as ProjectFile;
    if (typeof json.name === 'string' && json.name.trim()) name = json.name.trim();
  } catch {
    throw new Error('project.json is not valid JSON.');
  }
  return { files, name };
}

export function filesFromZip(zip: Uint8Array): Map<string, Uint8Array> {
  return new Map(Object.entries(unzipSync(zip)));
}

/** Files from a folder picked with `webkitdirectory` or dropped, by their relative paths. */
export async function filesFromFolder(list: Iterable<File & { webkitRelativePath?: string; relativePath?: string }>): Promise<Map<string, Uint8Array>> {
  const out = new Map<string, Uint8Array>();
  for (const f of list) {
    const path = f.relativePath || f.webkitRelativePath || f.name;
    out.set(path.replace(/^\/+/, ''), new Uint8Array(await f.arrayBuffer()));
  }
  return out;
}

/** A .zip of the project, with its files in a folder named after it. */
export function zipProject(name: string, files: Map<string, Uint8Array>): Uint8Array {
  const folder = slug(name);
  const tree: Zippable = {};
  for (const [path, bytes] of files) {
    // Images are already compressed; JSON shrinks a lot.
    tree[`${folder}/${path}`] = [bytes, { level: path.endsWith('.json') ? 6 : 0 }];
  }
  return zipSync(tree);
}

export function slug(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'project';
}

/** A new project with one empty scene and the guide for AI assistants, listing `plugins` (such as a kit picked for it). */
export function emptyProject(options: { name: string; width: number; height: number; grid: number; plugins?: string[] }): Map<string, Uint8Array> {
  const json = (value: unknown) => strToU8(`${JSON.stringify(value, null, 2)}\n`);
  return withGuide(new Map([
    [
      PROJECT_FILE,
      json({
        formatVersion: 1,
        name: options.name,
        root: 'public',
        resolution: { width: options.width, height: options.height },
        scenes: ['scenes/main.scene.json'],
        plugins: options.plugins ?? [],
        editor: { grid: { width: options.grid, height: options.grid }, snap: true },
      }),
    ],
    ['public/scenes/main.scene.json', json({ formatVersion: 1, key: 'Main', preload: [], children: [] })],
  ]));
}

/**
 * The project's files plus the guide for AI assistants (AGENTS.md, and a
 * CLAUDE.md that imports it) next to project.json, keeping any already there.
 */
export function withGuide(files: Map<string, Uint8Array>): Map<string, Uint8Array> {
  const bytes = files.get(PROJECT_FILE);
  const project = bytes ? (JSON.parse(new TextDecoder().decode(bytes)) as ProjectFile) : undefined;
  const out = new Map(files);
  for (const [name, text] of guideFiles(project?.root)) if (!out.has(name)) out.set(name, strToU8(text));
  return out;
}

/** The project's files with its name in project.json set to `name`. */
export function withName(files: Map<string, Uint8Array>, name: string): Map<string, Uint8Array> {
  const out = new Map(files);
  const bytes = files.get(PROJECT_FILE);
  if (!bytes) return out;
  try {
    const json = JSON.parse(new TextDecoder().decode(bytes)) as ProjectFile;
    out.set(PROJECT_FILE, strToU8(`${JSON.stringify({ ...json, name }, null, 2)}\n`));
  } catch {
    // Left as it is: the editor reports the broken file when it opens.
  }
  return out;
}

export interface ProjectSummary {
  scenes: number;
  images: number;
  tilemaps: number;
  bytes: number;
  /** The first image, for the project's thumbnail. */
  thumbnail?: string;
}

const IMAGE = /\.(png|jpe?g|webp|gif)$/i;

/** What the projects page shows for a project: counts of its scenes, images and tilemaps, and its size. */
export function summarize(files: Map<string, Uint8Array>): ProjectSummary {
  const summary: ProjectSummary = { scenes: 0, images: 0, tilemaps: 0, bytes: 0 };
  for (const [path, bytes] of [...files].sort(([a], [b]) => (a < b ? -1 : 1))) {
    summary.bytes += bytes.byteLength;
    if (path.endsWith('.scene.json')) summary.scenes++;
    else if (path.endsWith('.tilemap.json')) summary.tilemaps++;
    else if (IMAGE.test(path)) {
      summary.images++;
      summary.thumbnail ??= path;
    }
  }
  return summary;
}

/** The BroadcastChannel a project's editor tab and its game share for hot reload. */
export function liveChannel(id: string): string {
  return `animo-live:${id}`;
}

/**
 * Hands changes to a folder project's files, made by anything outside the
 * editor (an AI assistant, an image editor, git), to the open project, as the
 * dev server's file watcher does, and tells `scriptsChanged` when script files
 * changed. `paths` are relative to the folder holding
 * project.json. The project recognises its own saves by content.
 */
export function folderChanges(project: EditorProject, options: { debounceMs?: number; scriptsChanged?: () => void } = {}): (paths: string[]) => void {
  const { debounceMs = 50, scriptsChanged } = options;
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const prefix = rootPrefix(project.project);
  let scriptTimer: ReturnType<typeof setTimeout> | undefined;
  return (paths) => {
    if (scriptsChanged && paths.some((p) => scriptCandidate(p, prefix))) {
      clearTimeout(scriptTimer);
      scriptTimer = setTimeout(scriptsChanged, 100);
    }
    for (const path of paths) {
      let file: string;
      if (path === PROJECT_FILE) file = PROJECT_FILE;
      else if (path.startsWith(prefix)) file = path.slice(prefix.length);
      else continue;
      // Other files (images) aren't documents, but moving one can break links to it.
      if (!file.endsWith('.json')) {
        if (isShaderPath(file)) void project.textChanged(file);
        project.outsideChange();
        continue;
      }
      clearTimeout(timers.get(file));
      timers.set(
        file,
        setTimeout(() => {
          timers.delete(file);
          project.diskChanged(file).catch((e: Error) => project.emit('error', { file, message: e.message }));
        }, debounceMs),
      );
    }
  };
}
